export const OPERATIONS_SETTINGS_GROUP = "OPERATIONS";

export const OPERATIONS_LOW_ENROLLMENT_THRESHOLD_KEY =
  "OPERATIONS_LOW_ENROLLMENT_THRESHOLD";
export const OPERATIONS_TEACHER_LOAD_LIGHT_MAX_KEY =
  "OPERATIONS_TEACHER_LOAD_LIGHT_MAX";
export const OPERATIONS_TEACHER_LOAD_PART_TIME_MAX_KEY =
  "OPERATIONS_TEACHER_LOAD_PART_TIME_MAX";
export const OPERATIONS_TEACHER_LOAD_FULL_TIME_MAX_KEY =
  "OPERATIONS_TEACHER_LOAD_FULL_TIME_MAX";

export interface StudioOperationsSettings {
  lowEnrollmentThreshold: number;
  teacherLoadLightMax: number;
  teacherLoadPartTimeMax: number;
  teacherLoadFullTimeMax: number;
}

export const DEFAULT_STUDIO_OPERATIONS_SETTINGS: StudioOperationsSettings = {
  lowEnrollmentThreshold: 5,
  teacherLoadLightMax: 4,
  teacherLoadPartTimeMax: 12,
  teacherLoadFullTimeMax: 20,
};

export type StudioOperationsSettingsPersisted = {
  lowEnrollmentThreshold: boolean;
  teacherLoadLightMax: boolean;
  teacherLoadPartTimeMax: boolean;
  teacherLoadFullTimeMax: boolean;
};

export const DEFAULT_STUDIO_OPERATIONS_SETTINGS_PERSISTED: StudioOperationsSettingsPersisted =
  {
    lowEnrollmentThreshold: false,
    teacherLoadLightMax: false,
    teacherLoadPartTimeMax: false,
    teacherLoadFullTimeMax: false,
  };

const MIN_THRESHOLD = 1;
const MAX_THRESHOLD = 999;

export function parseOperationsThreshold(
  value: string | number | null | undefined,
  fallback: number,
): number {
  const parsed =
    typeof value === "number"
      ? value
      : Number.parseInt(String(value ?? "").trim(), 10);

  if (!Number.isFinite(parsed) || parsed < MIN_THRESHOLD || parsed > MAX_THRESHOLD) {
    return fallback;
  }

  return parsed;
}

export function isValidDraftOperationsThreshold(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const parsed = Number.parseInt(trimmed, 10);
  return (
    Number.isFinite(parsed) && parsed >= MIN_THRESHOLD && parsed <= MAX_THRESHOLD
  );
}

export function normalizeStudioOperationsSettings(
  source: Partial<StudioOperationsSettings> | null | undefined,
): StudioOperationsSettings {
  const defaults = DEFAULT_STUDIO_OPERATIONS_SETTINGS;
  const lightMax = parseOperationsThreshold(
    source?.teacherLoadLightMax,
    defaults.teacherLoadLightMax,
  );
  let partTimeMax = parseOperationsThreshold(
    source?.teacherLoadPartTimeMax,
    defaults.teacherLoadPartTimeMax,
  );
  let fullTimeMax = parseOperationsThreshold(
    source?.teacherLoadFullTimeMax,
    defaults.teacherLoadFullTimeMax,
  );

  if (partTimeMax <= lightMax) {
    partTimeMax = lightMax + 1;
  }
  if (fullTimeMax <= partTimeMax) {
    fullTimeMax = partTimeMax + 1;
  }

  return {
    lowEnrollmentThreshold: parseOperationsThreshold(
      source?.lowEnrollmentThreshold,
      defaults.lowEnrollmentThreshold,
    ),
    teacherLoadLightMax: lightMax,
    teacherLoadPartTimeMax: partTimeMax,
    teacherLoadFullTimeMax: fullTimeMax,
  };
}

export function validateStudioOperationsDraft(draft: {
  lowEnrollmentThreshold: string;
  teacherLoadLightMax: string;
  teacherLoadPartTimeMax: string;
  teacherLoadFullTimeMax: string;
}): {
  isValid: boolean;
  errors: Partial<Record<keyof typeof draft, string>>;
} {
  const errors: Partial<Record<keyof typeof draft, string>> = {};

  if (!isValidDraftOperationsThreshold(draft.lowEnrollmentThreshold)) {
    errors.lowEnrollmentThreshold = "Enter a number between 1 and 999.";
  }
  if (!isValidDraftOperationsThreshold(draft.teacherLoadLightMax)) {
    errors.teacherLoadLightMax = "Enter the maximum weekly sessions for light load.";
  }
  if (!isValidDraftOperationsThreshold(draft.teacherLoadPartTimeMax)) {
    errors.teacherLoadPartTimeMax =
      "Enter the maximum weekly sessions for part-time load.";
  }
  if (!isValidDraftOperationsThreshold(draft.teacherLoadFullTimeMax)) {
    errors.teacherLoadFullTimeMax =
      "Enter the maximum weekly sessions for full-time load.";
  }

  if (Object.keys(errors).length > 0) {
    return { isValid: false, errors };
  }

  const lightMax = Number.parseInt(draft.teacherLoadLightMax.trim(), 10);
  const partTimeMax = Number.parseInt(draft.teacherLoadPartTimeMax.trim(), 10);
  const fullTimeMax = Number.parseInt(draft.teacherLoadFullTimeMax.trim(), 10);

  if (partTimeMax <= lightMax) {
    errors.teacherLoadPartTimeMax =
      "Part-time max must be greater than light load max.";
  }
  if (fullTimeMax <= partTimeMax) {
    errors.teacherLoadFullTimeMax =
      "Full-time max must be greater than part-time max.";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
